/**
 * Deciding what the file list *is*, before anybody reads a byte of it.
 *
 * `collect.ts` already turns a list of `File` objects into a `GtaResourceInput`
 * with caps and an honest `truncated` list. What it cannot do is decide which
 * files came from where, which directories are resources, and what to do with an
 * archive — because that depends on the gesture the user made, and `collect.ts`
 * is deliberately below that question.
 *
 * This module is that question, answered once. It has to be answered once
 * because four separate tools in the competitor's catalogue answer it four
 * different wrong ways, and every one of those failures is a *confident wrong
 * answer* rather than an error:
 *
 *   1. Reading `dataTransfer.files` instead of walking `webkitGetAsEntry()`, so
 *      a dragged folder — the tool's own advertised gesture — reads as empty and
 *      the report says "No collisions. Every stream asset name and vehicle spawn
 *      code is unique across the drop."
 *   2. Concatenating every archive's paths into one flat list with no
 *      per-archive prefix and then deduping on path, so the second vendor zip's
 *      `police/stream/police.yft` *is* the first one — the precise collision the
 *      tool exists to find is the thing its own intake erases.
 *   3. Taking the first path in enumeration order and making its first segment
 *      the sole resource root whenever no manifest is found, so two packs merge
 *      into one, the summary reads "2 spawns across 1 pack", and every
 *      cross-resource rule becomes structurally unfirable.
 *   4. Keeping one path list with manifests already filtered out of it, then
 *      passing that list to a per-resource grouping function that looks for
 *      `fxmanifest.lua`. The branch cannot match. The flagship claim of the tool
 *      is false for exactly the input it is built for.
 *
 * Each of those is cheap to avoid and impossible to retrofit, because by the
 * time the analyzer runs, the information is already gone. So the four rules
 * below are enforced here, at the one place every folder tool enters through.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY `allPaths` AND `resources` BOTH EXIST, OUT OF ONE PASS
 *
 * They answer different questions and neither is derivable from the other.
 * `allPaths` is everything, manifests and scripts included, because a tool that
 * sizes or groups a folder has to count the things the analyzers ignore.
 * `resources` is the analysable subset, grouped and rooted, because a tool that
 * checks a resource needs to know where the resource starts. Defect 4 above is
 * what happens when a codebase keeps only one of them and hopes.
 *
 * WHY fflate AND NOT JSZip
 *
 * JSZip is what `CodeVisualizer` reads a zip with today, and it is the wrong
 * tool for an intake that has to *refuse* things. `JSZip.loadAsync(file)` reads
 * and holds the whole archive before anyone can ask how big it is, which means
 * the oversized-input check can only run after the allocation it exists to
 * prevent — that is how a 6 GB server backup becomes a `RangeError` that a
 * catch block launders into the six words "Failed to scan."
 *
 * fflate's `unzipSync` filter callback is handed each entry's `originalSize`
 * *before* deciding whether to inflate it, and returning `false` for everything
 * walks the central directory and inflates nothing. So the first pass over an
 * archive is a free listing: entry count and total uncompressed size, both known
 * before a single byte is decompressed, which is what lets the caps below reject
 * with a number in the message instead of a stack trace. The same filter then
 * does the second job for free: only entries that will actually be read as text
 * are inflated, so a 2 GB vehicle pack costs us its `.meta` and `.lua` files and
 * nothing else.
 *
 * `unzipSync` rather than the async `unzip` because the async path moves work to
 * a Worker built from a blob URL, which is unavailable under jsdom and is the
 * first thing a strict `script-src` blocks. Given that the only entries we
 * inflate are the text ones, the sync call is a central-directory walk plus a
 * few hundred kilobytes of inflate — not the multi-second block it sounds like.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS, STATED PLAINLY
 *
 * The honesty rule of this layer is that nothing may report a clean result for
 * input it did not read, so everything skipped, capped, refused or unreadable
 * comes back in `truncated` in words a user can act on, and the caller is
 * expected to show it. Specifically:
 *
 *  - Binary entries inside an archive are represented by their declared
 *    uncompressed size and an empty body. Their sizes are exact — that is what
 *    the zip's central directory is for — but their *bytes* are not there.
 *    Anything that needs to parse a `.yft` header out of an archive must read
 *    the archive itself; nothing in this layer does today.
 *  - An entry whose bytes we wanted and did not get — a zip written with LZMA,
 *    deflate64 or AES, which 7-Zip and WinRAR write by default on some
 *    profiles, or a truncated download — is inventory only. It keeps its real
 *    size in `allPaths` and is named in `truncated`, and it is deliberately
 *    *not* handed to `collect.ts`, because a `File` whose size says 6,800 bytes
 *    and whose body is empty decodes to `""` and the analyzer then reports a
 *    manifest that declares nothing. See `sizedPlaceholder`.
 *  - `.rpf` archives are not readable in a browser at all. They are counted in
 *    `allPaths` and named in `truncated`, never silently treated as zero.
 *  - Archives nest exactly one level. A zip inside a zip inside a zip is listed
 *    and said out loud, not opened.
 *  - Resource roots are structural, not semantic. This module says "here is a
 *    directory that looks like a resource"; `analyze.ts` is what judges it.
 *  - Two directories that differ only in case are one root, because the whole
 *    layer compares paths through `pathKey` and a FiveM server would load one
 *    of them. The *files* under both are kept — see `push` — so a case-only
 *    stream-asset clash, which is the one that costs a server, stays visible to
 *    the rules that exist to report it. Two *text* files that differ only in
 *    case are both inventoried here and then share one key in `collect.ts`'s
 *    `texts` map, so only one of the two is read; that is `collect.ts`'s
 *    contract, not something this module can fix from above.
 */

import { unzipSync, type Unzipped } from "fflate";

import { isManifestFileName } from "./analyze";
import type { CollectableFile } from "./collect";
import { TEXT_EXTENSIONS } from "./collect";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "./labels";
import { baseName, extensionOf, normalizePath, pathKey } from "./normalize";

// ─── The result ──────────────────────────────────────────────────────────────

export type DroppedResource = {
  /**
   * The resource's own name — its folder name.
   *
   * Deliberately *not* made unique. Two vendor zips that both ship `police/`
   * produce two resources both named `police`, and that duplicate name is a real
   * finding about a real server that cannot start both. `root` is the unique
   * identity inside a drop; `name` is what FiveM would call it.
   */
  name: string;
  /** Drop-relative directory the resource starts at. Unique within a drop. */
  root: string;
  /** Paths here are relative to `root`, which is the contract `collect.ts` wants. */
  files: CollectableFile[];
};

export type ResourceDrop = {
  resources: DroppedResource[];
  /** Everything, including manifests and scripts, for tools that size a folder. */
  allPaths: { path: string; size: number }[];
  /**
   * Which archive or pick each resource came from, keyed by `root`, so two zips
   * cannot cancel.
   *
   * Keyed by `root` and not by `name` precisely because `name` is allowed to
   * collide — keying this on the colliding thing is defect 2 in the header, and
   * it drops the second archive's spawn codes while still counting its assets.
   */
  origins: Map<string, string>;
  /** What was not read, in words a user can act on. Empty when nothing was. */
  truncated: string[];
  /**
   * Resources a caller took out of this drop by request, through
   * `excludeResources`. Absent on a drop nobody has excluded anything from,
   * which is every drop an entry point below returns.
   *
   * Kept as data and not only as the sentence `excludeResources` also writes
   * into `truncated`, because an export has to be able to list what was skipped
   * without parsing prose back out of a notice.
   */
  excluded?: ExcludedResource[];
};

/** A resource removed from a drop by request. Named, never silently gone. */
export type ExcludedResource = {
  name: string;
  /** `DroppedResource.root` — the identity the caller asked to skip. */
  root: string;
  origin?: string;
  /** The label the notice used, disambiguated against the whole drop. */
  label: string;
  /** Files under it that were not handed on to be read. Its inventory, not a guess. */
  files: number;
};

// ─── Caps ────────────────────────────────────────────────────────────────────

export type IntakeLimits = {
  /**
   * Compressed bytes of one archive, checked before `arrayBuffer()` is called.
   *
   * This is the cap that actually prevents the competitor's `RangeError`: the
   * throw happens at the allocation, so the check has to happen before it.
   */
  maxArchiveBytes: number;
  /** Declared uncompressed bytes across every archive in one drop. */
  maxUncompressedBytes: number;
  /** Entries across every archive in one drop. */
  maxEntries: number;
  /**
   * Largest archive entry inflated for its text.
   *
   * `collect.ts` caps a single text file at 4 MB and skips anything larger, so
   * inflating past this point is work whose result is guaranteed to be thrown
   * away. The number is duplicated from `collect.ts` on purpose and that file is
   * the source of truth; if the two ever drift the only cost is wasted work,
   * never a wrong answer.
   */
  maxTextEntryBytes: number;
  /**
   * Entries read out of one dragged directory.
   *
   * A loop guard, not a policy. `readEntries` is specified to return an empty
   * array when it is done, and a browser that never does would otherwise spin
   * forever — see `readAllEntries`.
   */
  maxDirectoryEntries: number;
};

export const INTAKE_LIMITS: IntakeLimits = {
  maxArchiveBytes: 1024 * 1024 * 1024,
  maxUncompressedBytes: 2 * 1024 * 1024 * 1024,
  maxEntries: 50_000,
  maxTextEntryBytes: 4 * 1024 * 1024,
  maxDirectoryEntries: 200_000,
};

/**
 * How many levels an archive may nest and still be opened.
 *
 * 1 means a zip of vendor zips works — the outer opens, each inner opens, and a
 * third level is listed and said out loud rather than opened. A user who zips
 * their downloads folder is doing the ordinary thing, and the competitor's
 * single-archive fast path does not recurse at all, which is why that user gets
 * `Stream assets 0 / Collisions 0` instead of a report.
 *
 * `depth` throughout this module means "how many archives this file is inside":
 * 0 for a file in the drop, 1 for a file inside a dropped zip, 2 for a file
 * inside a zip inside a zip.
 */
const MAX_ARCHIVE_NESTING = 1;

/** Openable here. Everything else is inventory plus a sentence in `truncated`. */
const ZIP_EXTENSIONS: ReadonlySet<string> = new Set(["zip"]);

/**
 * Recognisable, and not readable in a browser.
 *
 * `.rpf` is the one that matters: a pack distributed as `dlc.rpf` is invisible
 * to every tool in this layer, and the plan's own non-goals say such a pack must
 * be *said* rather than reported as zero.
 */
const UNREADABLE_ARCHIVE_EXTENSIONS: ReadonlySet<string> = new Set(["rpf", "rar", "7z"]);

export type IntakeOptions = {
  /**
   * Cancels the scan.
   *
   * Checked between archives, between entries and between directory batches, so
   * a user who dropped a server backup by mistake can stop it instead of killing
   * the tab. Aborting rejects rather than returning a partial drop: a half-read
   * folder that looks like a whole one is the failure this layer exists to
   * prevent.
   */
  signal?: AbortSignal;
  /**
   * Overrides for the caps above. Tests use it to exercise refusal paths without
   * building a two-gigabyte fixture; a desktop caller with more memory may raise
   * them. Anything omitted keeps its default.
   */
  limits?: Partial<IntakeLimits>;
};

// ─── The structural minimum of a drag-drop event ─────────────────────────────

/**
 * jsdom implements neither `DataTransfer` nor `webkitGetAsEntry`, and the file
 * system entry API is non-standard enough that TypeScript's own `FileSystemEntry`
 * is a base type with no `isFile: true` discriminant to narrow on.
 *
 * So the drag-drop entry point is typed against the structural minimum it
 * actually touches. A real `DataTransfer` is assignable to `DataTransferLike`
 * and a real `FileSystemEntry` to `DropEntry`, so `fromDataTransfer(e.dataTransfer)`
 * compiles unchanged at every real call site — this is the brief's signature with
 * the DOM types widened, not weakened, and nothing here is `any`.
 */
export type DropDirectoryReader = {
  readEntries(onSuccess: (entries: DropEntry[]) => void, onError?: (error: unknown) => void): void;
};

export type DropEntry = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath?: string;
  file?(onSuccess: (file: File) => void, onError?: (error: unknown) => void): void;
  createReader?(): DropDirectoryReader;
};

export type DataTransferItemLike = {
  kind: string;
  getAsFile(): File | null;
  webkitGetAsEntry?(): DropEntry | null;
};

export type DataTransferLike = {
  items?: ArrayLike<DataTransferItemLike> | null;
  files?: ArrayLike<File> | null;
};

// ─── Internals ───────────────────────────────────────────────────────────────

/** One file on its way in: already namespaced, already attributed. */
type GatheredFile = {
  /** Drop-relative and normalized by `dropRelative`. Unique across the drop. */
  path: string;
  /** Uncompressed size in bytes, from the archive's central directory or `File.size`. */
  size: number;
  /** The container it came out of, for `origins`. */
  origin: string;
  file: File;
  /**
   * Whether `file` may be handed to `collect.ts`, or is inventory only.
   *
   * `false` means the size is real and the bytes are not, *and* something
   * downstream would have read those bytes — a text entry we could not
   * decompress, or one we refused to inflate because it is over the cap. Such
   * an entry stays in `allPaths`, where only its size is read, and is kept out
   * of `resources`, where `collect.ts` would call `.text()` on an empty body
   * and store `""` as the file's content. That is the difference between "this
   * manifest was not read" and "this manifest declares nothing", and the second
   * one is a confident wrong answer the analyzer cannot see through.
   *
   * Binary entries are `true`: their empty body is never read, because
   * `collect.ts` only decodes `TEXT_EXTENSIONS`, so their exact size is all
   * anyone asks of them.
   */
  analysable: boolean;
};

type Gather = {
  limits: IntakeLimits;
  signal: AbortSignal | undefined;
  files: GatheredFile[];
  seen: Set<string>;
  truncated: string[];
  uncompressedUsed: number;
  entriesUsed: number;
};

function newGather(options: IntakeOptions | undefined): Gather {
  return {
    limits: { ...INTAKE_LIMITS, ...options?.limits },
    signal: options?.signal,
    files: [],
    seen: new Set<string>(),
    truncated: [],
    uncompressedUsed: 0,
    entriesUsed: 0,
  };
}

function ensureNotAborted(signal: AbortSignal | undefined): void {
  if (!signal?.aborted) return;
  if (typeof signal.throwIfAborted === "function") signal.throwIfAborted();
  throw new DOMException("The scan was cancelled.", "AbortError");
}

/**
 * Drop-relative, normalized, and never rooted at a separator.
 *
 * `normalizePath` leaves a leading `/` alone on purpose — an absolute path is
 * meaningful elsewhere in the layer — but inside a drop it is not: every path
 * here is relative to the drop. A zip entry stored as `/police/fxmanifest.lua`
 * (Java's `ZipOutputStream` writes these, so do hand-rolled backup scripts, and
 * a Windows packer's leading `\` normalises to the same thing) would otherwise
 * give that one file a root one character longer than its siblings', and every
 * sibling's relative path would then lose its first character: `vehicles.meta`
 * arrives at the analyzer as `ehicles.meta`, which no rule looks up, so the
 * pack reports zero spawn codes and zero stream assets and no warning at all.
 *
 * Every path in a `GatheredFile` has been through here, which is what lets the
 * assembly pass index roots by `pathKey` and slice relative paths by offset
 * without the two disagreeing about how long the prefix is.
 */
function dropRelative(path: string): string {
  return normalizePath(path).replace(/^\/+/, "");
}

/**
 * Add a file to the drop, once.
 *
 * Keyed on the exact path and deliberately *not* on `pathKey`. Case is the
 * difference between `stream/police.yft` and `stream/Police.yft`, and FiveM
 * hashes streamed asset names case-insensitively — so those two files are
 * precisely the collision this layer exists to report, and a pack authored on
 * Windows then unzipped and re-zipped on Linux really does ship both.
 * Collapsing them here would delete the evidence before any rule could see it,
 * which is defect 2 from the header one level down. `normalize.ts` ships
 * `caseOnlyMatch` because the layer treats case-only path differences as a real
 * bug class, not as noise.
 *
 * A genuinely identical path is still dropped — reading the same bytes twice
 * would double every count — but it is said out loud, because silently
 * discarding input is the one thing this module may not do.
 */
function push(gather: Gather, entry: GatheredFile): void {
  if (gather.seen.has(entry.path)) {
    gather.truncated.push(
      `${entry.path} appears in this drop more than once, so only the first copy was read.`,
    );
    return;
  }
  gather.seen.add(entry.path);
  gather.files.push(entry);
}

function bytes(count: number): string {
  if (count >= 1024 * 1024 * 1024) return `${(count / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (count >= 1024 * 1024) return `${(count / (1024 * 1024)).toFixed(1)} MB`;
  if (count >= 1024) return `${(count / 1024).toFixed(1)} KB`;
  return `${count} bytes`;
}

/**
 * A file whose size is real and whose bytes are not.
 *
 * A zip's central directory states every entry's uncompressed size, so the size
 * is exact without inflating anything — and inflating a 900 MB `.ytd` to answer
 * "how big is it" is the single most reliable way to take the tab down.
 *
 * The empty body is only honest while nobody reads it, and that is a property of
 * the *caller*, not of this function. `collect.ts` calls `.text()` on every file
 * whose extension is in `TEXT_EXTENSIONS` and under 4 MB; it has no way to tell
 * an empty file from an unread one, so a placeholder for a text entry decodes to
 * `""` and the analyzer reports a manifest that declares nothing — on a manifest
 * that is perfectly fine. So a placeholder stands in for a *binary* entry only,
 * and every text entry we could not inflate is marked `analysable: false` and
 * kept out of `resources` instead. See `GatheredFile.analysable`.
 */
function sizedPlaceholder(name: string, size: number): File {
  const file = new File([], name);
  if (size > 0) Object.defineProperty(file, "size", { value: size, configurable: true });
  return file;
}

// ─── Rule 1: drag-drop walks entries, in batches, to the end ─────────────────

/**
 * Every entry in a directory, not the first hundred of them.
 *
 * `readEntries` returns a *batch*. Chrome's is 100 entries; the spec's only
 * promise is that an empty array means the end. A single call therefore reads
 * the first hundred files of a 4,000-file vehicle pack and reports everything
 * else as absent — which for a conflict finder means the conflicts are in the
 * part it never looked at, and the answer is a green all-clear. That is the
 * exact shape of the competitor's Asset Conflict Finder failure, on its own
 * advertised gesture.
 *
 * So: keep calling until an empty array comes back. The counter is a loop guard
 * against a reader that never terminates, and it reports itself rather than
 * quietly stopping.
 *
 * A reader that fails part-way stops *this folder* and nothing else. Chrome
 * fires the error callback with a `NotFoundError` when a directory is moved,
 * renamed or deleted while it is being read, and on a permission fault over a
 * network share — and a long scan of a big pack is exactly the window in which
 * that happens. Propagating it would reject the whole drag-drop and throw away
 * every resource already gathered, leaving the caller with the generic
 * "Failed to scan." this module is written against; so the failure is reported
 * the way `entryToFile`'s already is, by name, in `truncated`, with the rest of
 * the drop intact. A cancel still rejects: that is the one abort this module
 * treats as fatal, because a half-read folder that looks like a whole one is
 * worse than no answer.
 */
async function readAllEntries(
  reader: DropDirectoryReader,
  directoryPath: string,
  gather: Gather,
): Promise<DropEntry[]> {
  const all: DropEntry[] = [];
  const label = directoryPath || "The dropped folder";
  for (;;) {
    ensureNotAborted(gather.signal);
    let batch: DropEntry[];
    try {
      batch = await new Promise<DropEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
    } catch {
      ensureNotAborted(gather.signal);
      gather.truncated.push(
        all.length === 0
          ? `${label} could not be read, so nothing in it was checked.`
          : `${label} could not be read past its first ${all.length.toLocaleString()} entries, ` +
            "so the rest of it was not checked.",
      );
      return all;
    }
    if (batch.length === 0) return all;
    all.push(...batch);
    if (all.length > gather.limits.maxDirectoryEntries) {
      gather.truncated.push(
        `${directoryPath || "The dropped folder"} has more than ` +
          `${gather.limits.maxDirectoryEntries.toLocaleString()} entries, so the rest of it was not read.`,
      );
      return all;
    }
  }
}

function entryToFile(entry: DropEntry): Promise<File | null> {
  const read = entry.file;
  if (typeof read !== "function") return Promise.resolve(null);
  return new Promise<File | null>((resolve) => {
    read.call(
      entry,
      (file) => resolve(file),
      () => resolve(null),
    );
  });
}

/**
 * `path` is the entry's own drop-relative path, not its parent's.
 *
 * Passing the whole path in rather than deriving it from `entry.name` is what
 * lets `fromDataTransfer` namespace a dropped item whose name collides with
 * another dropped item — see rule 2 there.
 */
async function walkEntry(
  entry: DropEntry,
  path: string,
  origin: string,
  gather: Gather,
): Promise<void> {
  ensureNotAborted(gather.signal);

  if (entry.isDirectory) {
    const createReader = entry.createReader;
    if (typeof createReader !== "function") {
      gather.truncated.push(`${path} is a folder this browser would not open, so it was not read.`);
      return;
    }
    const children = await readAllEntries(createReader.call(entry), path, gather);
    for (const child of children) {
      await walkEntry(child, dropRelative(`${path}/${child.name}`), origin, gather);
    }
    return;
  }

  const file = await entryToFile(entry);
  if (!file) {
    gather.truncated.push(`${path} could not be opened, so it was not read.`);
    return;
  }
  await addFile(file, path, origin, gather, 0);
}

// ─── Archives ────────────────────────────────────────────────────────────────

/**
 * Add one file, opening it first if it is an archive we can open.
 *
 * `depth` is the file's own nesting depth, so a zip at depth `d` holds entries
 * at depth `d + 1` and is opened only while `d <= MAX_ARCHIVE_NESTING`.
 */
async function addFile(
  file: File,
  path: string,
  origin: string,
  gather: Gather,
  depth: number,
  /**
   * The origin to give this archive's *contents*, when it is not their path.
   *
   * Normally an archive's entries are attributed to the archive's path, which is
   * also its name — and for a zip inside a zip that path is exactly right:
   * `bundle.zip/vendor.zip` says where the resource is. It is wrong in one case,
   * and that case is why this exists: `fromArchives` namespaces the second of
   * two identically-named archives as `archive-2/police.zip` so their entries do
   * not land on the same paths, and `archive-2` is a segment this module made
   * up. Printing it to a user as the file their resource came out of would be
   * inventing provenance, which is the one thing this layer does not do. The
   * root keeps the segment, because the root is a key; the origin does not,
   * because the origin is an answer.
   */
  contentsOrigin?: string,
): Promise<void> {
  ensureNotAborted(gather.signal);
  const extension = extensionOf(path);

  if (ZIP_EXTENSIONS.has(extension)) {
    // The archive itself stays in allPaths: a tool sizing the drop must count
    // the bytes the user actually has on disk.
    push(gather, { path, size: file.size, origin, file, analysable: true });
    if (depth > MAX_ARCHIVE_NESTING) {
      gather.truncated.push(
        `${path} is an archive nested more than ${MAX_ARCHIVE_NESTING} level deep, ` +
          "so its contents were not read. Unpack it and try again.",
      );
      return;
    }
    await readArchive(file, path, gather, depth + 1, contentsOrigin ?? path);
    return;
  }

  if (UNREADABLE_ARCHIVE_EXTENSIONS.has(extension)) {
    push(gather, { path, size: file.size, origin, file, analysable: true });
    gather.truncated.push(
      `${path} is a .${extension} archive, which cannot be opened in a browser. ` +
        "Anything inside it was not checked — this is not a clean result for that file.",
    );
    return;
  }

  push(gather, { path, size: file.size, origin, file, analysable: true });
}

/** Everything the central directory says, before anything is inflated. */
type ArchiveEntry = { name: string; originalSize: number };

function listArchive(data: Uint8Array): ArchiveEntry[] {
  const entries: ArchiveEntry[] = [];
  unzipSync(data, {
    filter: (entry) => {
      // Directory records carry no bytes and no information we need: the paths
      // of the files inside them say the same thing.
      if (!entry.name.endsWith("/") && !entry.name.endsWith("\\")) {
        entries.push({ name: entry.name, originalSize: entry.originalSize });
      }
      return false;
    },
  });
  return entries;
}

/**
 * Rule 2: an archive's contents are namespaced by the archive.
 *
 * Every path gains the archive's own drop-relative path as a prefix, so two
 * vendor zips with identical internal layouts produce two disjoint sets of paths
 * and therefore two resources. The competitor concatenates both archives into
 * one flat list and dedupes on the relative path, which means the second zip's
 * `police/stream/police.yft` silently *is* the first zip's — the collision the
 * tool exists to report is destroyed by the code that loads the input. Its
 * per-vehicle map is keyed on the same relative path, so the second archive's
 * spawn codes are dropped outright while the asset counter still counts both.
 */
async function readArchive(
  file: File,
  archivePath: string,
  gather: Gather,
  /** Nesting depth of the entries inside this archive, not of the archive itself. */
  entryDepth: number,
  /** What to call this archive when a finding names it. See `addFile`. */
  origin: string = archivePath,
): Promise<void> {
  ensureNotAborted(gather.signal);

  if (file.size > gather.limits.maxArchiveBytes) {
    gather.truncated.push(
      `${archivePath} is ${bytes(file.size)}, over the ${bytes(gather.limits.maxArchiveBytes)} ` +
        "limit for a single archive, so it was not opened. Unpack it and drop the folder instead.",
    );
    return;
  }

  let data: Uint8Array;
  try {
    data = new Uint8Array(await file.arrayBuffer());
  } catch {
    gather.truncated.push(`${archivePath} could not be read into memory, so it was not opened.`);
    return;
  }

  ensureNotAborted(gather.signal);

  let entries: ArchiveEntry[];
  try {
    entries = listArchive(data);
  } catch {
    gather.truncated.push(
      `${archivePath} is not a readable zip archive, so nothing inside it was checked.`,
    );
    return;
  }

  // Both ceilings are checked against the whole drop and before any inflate, so
  // the refusal names a number the user can do something about instead of
  // becoming a RangeError that a catch block turns into "Failed to scan."
  if (gather.entriesUsed + entries.length > gather.limits.maxEntries) {
    gather.truncated.push(
      `${archivePath} holds ${entries.length.toLocaleString()} entries, which takes this drop over ` +
        `the ${gather.limits.maxEntries.toLocaleString()}-entry limit, so it was not opened.`,
    );
    return;
  }

  const uncompressed = entries.reduce((total, entry) => total + entry.originalSize, 0);
  if (gather.uncompressedUsed + uncompressed > gather.limits.maxUncompressedBytes) {
    gather.truncated.push(
      `${archivePath} unpacks to ${bytes(uncompressed)}, which takes this drop over the ` +
        `${bytes(gather.limits.maxUncompressedBytes)} limit, so it was not opened.`,
    );
    return;
  }

  gather.entriesUsed += entries.length;
  gather.uncompressedUsed += uncompressed;

  // Which entries are worth the inflate: the ones `collect.ts` will read as
  // text, and nested archives, which cannot be opened without their bytes.
  const wanted = new Set<string>();
  const tooLargeForText = new Set<string>();
  for (const entry of entries) {
    const extension = extensionOf(dropRelative(entry.name));
    // A nested archive has to be inflated or it cannot be opened; the depth test
    // here is the same one `addFile` applies, so an archive too deep to open is
    // never inflated for nothing.
    const isNested = ZIP_EXTENSIONS.has(extension) && entryDepth <= MAX_ARCHIVE_NESTING;
    if (isNested) {
      wanted.add(entry.name);
      continue;
    }
    if (!TEXT_EXTENSIONS.has(extension)) continue;
    if (entry.originalSize > gather.limits.maxTextEntryBytes) {
      tooLargeForText.add(entry.name);
      continue;
    }
    wanted.add(entry.name);
  }

  if (tooLargeForText.size > 0) {
    const count = tooLargeForText.size;
    gather.truncated.push(
      `${count} text file${count === 1 ? "" : "s"} in ${archivePath} ` +
        `${count === 1 ? "is" : "are"} over ${bytes(gather.limits.maxTextEntryBytes)}, ` +
        "so anything they declare was not checked.",
    );
  }

  let inflated: Unzipped = {};
  if (wanted.size > 0) {
    try {
      inflated = unzipSync(data, { filter: (entry) => wanted.has(entry.name) });
    } catch {
      gather.truncated.push(
        `${archivePath} uses a compression method this reader does not support, ` +
          "so its text files were not read.",
      );
    }
  }

  ensureNotAborted(gather.signal);

  // Entries we meant to read and have no bytes for. The listing pass above
  // never decompresses anything, so an archive written with LZMA, deflate64 or
  // AES — all of which 7-Zip and WinRAR write happily, and a truncated download
  // behaves the same — lists perfectly and then throws on the single inflate
  // call, leaving every text entry bodyless. Those entries are named here and
  // marked `analysable: false` below rather than handed to `collect.ts` as a
  // sized-but-empty `File`, which is what turned a valid manifest into four
  // confident findings about a manifest that "declares nothing".
  const undecompressed: string[] = [];

  for (const entry of entries) {
    // Backslash-separated entry names normalise here, through the one
    // normalizer, and nowhere else. A zip written by a Windows packer stores
    // `police\stream\police.yft`; compared raw there is no `stream/` segment, so
    // the file is attributed to no resource and no warning is raised. The
    // competitor degrades silently on exactly this input.
    const relative = dropRelative(entry.name);
    if (relative === "") continue;
    // Normalised at the join, not after it: `${archivePath}/${relative}` where
    // the entry was stored as `/police/fxmanifest.lua` is a path with a doubled
    // separator, and one path in a drop that is spelled differently from its
    // siblings mis-roots the whole resource. See `dropRelative`.
    const path = dropRelative(`${archivePath}/${relative}`);
    const name = baseName(relative);
    const body = inflated[entry.name];
    const entryFile = body ? new File([body], name) : sizedPlaceholder(name, entry.originalSize);
    // An inflated entry is a `Uint8Array`, and an empty one is still an object,
    // so this separates "we have no bytes" from "the file is empty" — which is
    // the whole distinction the rest of this branch turns on.
    const missing = !body && (wanted.has(entry.name) || tooLargeForText.has(entry.name));
    if (missing && wanted.has(entry.name)) undecompressed.push(relative);

    // Nested archives and unreadable ones both go back through `addFile`, so the
    // depth rule and the ".rpf cannot be opened" sentence are written once —
    // unless we never got the archive's bytes, in which case there is nothing to
    // open and saying "not a readable zip archive" about it would be a second
    // wrong answer on top of the first.
    if (
      !missing &&
      (ZIP_EXTENSIONS.has(extensionOf(relative)) ||
        UNREADABLE_ARCHIVE_EXTENSIONS.has(extensionOf(relative)))
    ) {
      await addFile(entryFile, path, origin, gather, entryDepth);
      continue;
    }

    push(gather, {
      path,
      size: entry.originalSize,
      origin,
      file: entryFile,
      analysable: !missing,
    });
  }

  if (undecompressed.length > 0) {
    const shown = undecompressed.slice(0, 5).join("; ");
    const rest =
      undecompressed.length > 5 ? ` and ${(undecompressed.length - 5).toLocaleString()} more` : "";
    gather.truncated.push(
      `${undecompressed.length.toLocaleString()} file${undecompressed.length === 1 ? "" : "s"} in ` +
        `${archivePath} could not be decompressed, so nothing they declare was checked: ` +
        `${shown}${rest}.`,
    );
  }
}

// ─── Rule 3: resource roots ──────────────────────────────────────────────────

function directoryOf(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash === -1 ? "" : path.slice(0, slash);
}

/** `police/stream/x.yft` → `police`, `""` when no segment is `stream`. */
function streamParentOf(path: string): string | null {
  const segments = normalizePath(path).split("/");
  const index = segments.findIndex((segment) => segment.toLowerCase() === "stream");
  if (index === -1) return null;
  return segments.slice(0, index).join("/");
}

/**
 * Directories a `.meta` file implies a resource at.
 *
 * A resource that keeps its metadata in a `data/` or `meta/` subfolder is the
 * common enough layout that treating that subfolder as the resource root would
 * mis-root most half-extracted downloads, so one level of that wrapper is
 * stepped over. Deeper or differently named nesting is not guessed at: this is
 * a fallback for drops with no manifest anywhere, and a wrong root is worse than
 * a shallow one.
 */
const META_WRAPPER_DIRECTORIES: ReadonlySet<string> = new Set(["data", "meta", "metas", "metadata"]);

function metaParentOf(path: string): string {
  const directory = directoryOf(normalizePath(path));
  const leaf = baseName(directory).toLowerCase();
  return META_WRAPPER_DIRECTORIES.has(leaf) ? directoryOf(directory) : directory;
}

/**
 * The deepest directory strictly above `start` that is a root, and where its
 * name ends in `key`.
 *
 * The walk is upward — for `police/stream/x.yft`: `police/stream`, `police`,
 * `""` — one hash lookup per level, so O(depth) per file. The obvious
 * alternative, testing every known root against every file, is O(files × roots):
 * on an ordinary server `resources/` tree of three hundred packs and eighteen
 * thousand files that is 5.4 million prefix comparisons, each of which
 * re-normalises both sides. Measured here on that input, the scan took 3,164 ms
 * of blocked main thread against 101 ms for this walk — and because both factors
 * grow with the drop, doubling the input quadruples the scan while it merely
 * doubles the walk. Worse, assembly is one synchronous block with no `await` in
 * it, so the `AbortSignal` that exists "so a scan of a server backup can be
 * stopped rather than killing the tab" could not interrupt the one phase that
 * was killing the tab.
 *
 * "Strictly above `start`" is also what keeps a root from claiming itself: a
 * caller passes `key.length`, so the first directory considered is the file's
 * own parent, and passes back a previous `cut` to resume the walk further up.
 *
 * The returned `cut` is an offset into `key`, and callers slice the original
 * path by it. That is only sound because every path has been through
 * `dropRelative` and `pathKey` only lower-cases, so a path and its key are the
 * same length — the invariant that a raw `${archive}/${entry}` join used to
 * break.
 */
function deepestRootAt(
  key: string,
  roots: ReadonlyMap<string, string>,
  start: number,
): { root: string; cut: number } | null {
  for (let cut = start; ; ) {
    cut = cut <= 0 ? -1 : key.lastIndexOf("/", cut - 1);
    const root = roots.get(cut <= 0 ? "" : key.slice(0, cut));
    if (root !== undefined) return { root, cut: cut <= 0 ? -1 : cut };
    if (cut <= 0) return null;
  }
}

/**
 * Every directory that looks like a resource, and never one synthetic root.
 *
 * A manifest is the definitive signal, so every directory holding one is a root.
 * When a subtree has no manifest anywhere above it — a stream-only pack, a
 * half-extracted download, a folder somebody unzipped one level too deep — the
 * fallback is any directory that directly contains a `stream/` folder or a
 * `.meta` file.
 *
 * The competitor instead takes the first path in enumeration order and makes its
 * first segment the sole root whenever no manifest is found. Two packs then
 * merge into one, the summary reads "2 spawns across 1 pack", and every
 * cross-resource rule — which by definition needs more than one resource — can
 * never fire. There is no input for which that is the right answer.
 *
 * A manifest nested inside another manifest's folder stays a root of its own.
 * FiveM would never start it, which is a finding `analyze.ts` already raises;
 * hiding it here would be this module deciding something it does not know.
 */
function resourceRootsOf(paths: string[]): string[] {
  // Keyed by `pathKey`, holding the spelling first seen: two directories that
  // differ only in case are one root, because every comparison in this layer
  // runs through `pathKey` and a server would load one of the two. The files
  // under both still survive — `push` keeps them — and land in that one
  // resource, where a case-only asset clash is exactly what the rules look for.
  const roots = new Map<string, string>();
  const manifestRoots = new Map<string, string>();
  for (const path of paths) {
    if (!isManifestFileName(baseName(path))) continue;
    const root = directoryOf(path);
    const key = pathKey(root);
    if (!manifestRoots.has(key)) manifestRoots.set(key, root);
    if (!roots.has(key)) roots.set(key, root);
  }

  for (const path of paths) {
    const stream = streamParentOf(path);
    let candidate: string | null = stream;
    if (candidate === null && extensionOf(path) === "meta") candidate = metaParentOf(path);
    if (candidate === null) continue;
    // A `stream/` folder inside a resource that has a manifest is that
    // resource's stream folder, not a second resource. Asked by walking the
    // candidate's own ancestors rather than by scanning every manifest root, for
    // the reason set out on `deepestRootAt`.
    const key = pathKey(candidate);
    if (manifestRoots.has(key)) continue;
    if (deepestRootAt(key, manifestRoots, key.length) !== null) continue;
    if (!roots.has(key)) roots.set(key, candidate);
  }

  return [...roots.values()].sort((a, b) => a.localeCompare(b));
}

/** The resource name a root implies, with an archive's extension taken off. */
function resourceNameOf(root: string): string {
  const leaf = baseName(root);
  if (leaf === "") return root === "" ? "drop" : root;
  const extension = extensionOf(leaf);
  if (ZIP_EXTENSIONS.has(extension) || UNREADABLE_ARCHIVE_EXTENSIONS.has(extension)) {
    return leaf.slice(0, leaf.length - extension.length - 1);
  }
  return leaf;
}

// ─── Assembly ────────────────────────────────────────────────────────────────

function assemble(gather: Gather): ResourceDrop {
  // The gather loops check the signal as they go; this one is here because an
  // abort raised between the last file and this line would otherwise produce a
  // complete-looking drop for a scan the user cancelled.
  ensureNotAborted(gather.signal);

  // Sorted, for the same reason `findings.ts` sorts: a list that comes back in
  // whatever order the browser walked the directory reshuffles between two scans
  // of the same folder, and a report that will not sit still is one nobody
  // trusts. It also makes a drag-drop and a folder pick of the same folder
  // comparable, which is a test in `intake.test.ts`.
  const files = [...gather.files].sort((a, b) => a.path.localeCompare(b.path));

  // Rule 4: unfiltered. Manifests, scripts, archives, files that belong to no
  // resource at all. A tool that sizes or groups a folder reads this list, and
  // the moment it is pre-filtered, a per-resource grouping that looks for
  // `fxmanifest.lua` becomes unreachable code — which is precisely the state the
  // competitor's Server Load Checker ships in.
  const allPaths = files.map((entry) => ({ path: entry.path, size: entry.size }));

  const roots = resourceRootsOf(files.map((entry) => entry.path));
  const origins = new Map<string, string>();
  const buckets = new Map<string, CollectableFile[]>();
  // One index over the roots, built once, instead of a scan of all of them per
  // file. `resourceRootsOf` has already made the keys unique.
  const rootsByKey = new Map<string, string>();
  for (const root of roots) {
    buckets.set(root, []);
    rootsByKey.set(pathKey(root), root);
  }

  for (const entry of files) {
    ensureNotAborted(gather.signal);
    // Inventory-only entries — the ones whose bytes we could not get — are
    // counted in `allPaths` above and stop here. See `GatheredFile.analysable`.
    if (!entry.analysable) continue;

    const key = pathKey(entry.path);
    // A manifest is the one file that is also listed in the resources it is
    // nested inside. Deepest root wins for everything else, so that a nested
    // resource's stream assets and spawn codes are counted once rather than
    // twice — but applying that to the manifest itself makes
    // `manifest.nested_resource` structurally unfirable: that rule reads a
    // manifest with a `/` in its path out of a resource's own file list, and
    // deepest-wins is what removes it from the parent. The parent then reports
    // clean, the child reports clean, and the server silently starts one of the
    // two. One extra inventory entry costs nothing: a manifest is not a stream
    // asset and declares no spawn code in its parent's namespace.
    const isManifest = isManifestFileName(baseName(entry.path));

    for (let start = key.length; ; ) {
      const hit = deepestRootAt(key, rootsByKey, start);
      if (hit === null) break;
      const relative = hit.cut === -1 ? entry.path : entry.path.slice(hit.cut + 1);
      buckets.get(hit.root)?.push({ path: relative, file: entry.file });
      if (!origins.has(hit.root)) origins.set(hit.root, entry.origin);
      if (!isManifest || hit.cut === -1) break;
      start = hit.cut;
    }
  }

  const resources: DroppedResource[] = [];
  for (const root of roots) {
    const bucket = buckets.get(root) ?? [];
    // A root with nothing under it is an artefact of the deepest-wins pass, not
    // a resource. Reporting it would inflate the pack count.
    if (bucket.length === 0) {
      origins.delete(root);
      continue;
    }
    resources.push({
      name: resourceNameOf(root),
      root,
      files: bucket.sort((a, b) => a.path.localeCompare(b.path)),
    });
  }

  const truncated = [...gather.truncated];
  // Only when nothing in the drop *looked* like a resource. Roots that were
  // found and then emptied — every file under them turned out to be one we
  // could not read — already have their reason in `truncated`, by name, and
  // telling that user there is no fxmanifest.lua here would be a second wrong
  // answer about the manifest we just said we could not decompress.
  if (resources.length === 0 && files.length > 0 && roots.length === 0) {
    truncated.push(
      "No resource was found in this drop: nothing in it contains an fxmanifest.lua, " +
        "a stream/ folder or a .meta file. Check you dropped the resource folder itself " +
        "rather than the folder above it.",
    );
  }

  return { resources, allPaths, origins, truncated: [...new Set(truncated)] };
}

// ─── Excluding resources ─────────────────────────────────────────────────────

/**
 * The same drop with some resources taken out, each one named in `truncated`.
 *
 * ── Why this is a step after intake and not an intake option ────────────────
 *
 * The competitor ID Bench replaces honoured its skip list on one of its three
 * intake paths, so the same tree through three buttons produced three plans and
 * three sets of allocated ids. A skip list applied *here*, to the `ResourceDrop`
 * every entry point already converges on, cannot differ by gesture: there is
 * one place it runs, and it runs after the three paths have become one shape.
 *
 * It is also the only shape that lets a user change their mind. A skip list
 * passed into `fromDataTransfer` would need the drop event again to re-run, and
 * a `DataTransfer` does not outlive its event. A caller holding the drop can
 * call this as often as the user ticks a box, and `collect.ts` re-reads only
 * what is left.
 *
 * ── What it keeps, and why ───────────────────────────────────────────────────
 *
 * Keyed on `root`, never `name`: two resources both called `police` are two
 * resources, and skipping "police" by name would skip both — the name collision
 * this whole module is written to keep visible. A root the drop does not hold
 * is ignored; there is nothing there to name.
 *
 * `allPaths` is left exactly as it was. It is the drop's inventory, rule 4 says
 * it is unfiltered, and a sizing tool that wants the excluded bytes out of its
 * total has `excluded` to do that with — deciding it here would make a folder's
 * size depend on which tool measured it.
 *
 * Every excluded resource gets a sentence in `truncated`, in the drop's own
 * honesty channel, so a surface that already shows `truncated` — every one in
 * this layer — states the exclusion without learning that exclusion exists.
 * Composes: excluding from an already-excluded drop appends.
 */
export function excludeResources(drop: ResourceDrop, roots: Iterable<string>): ResourceDrop {
  const skip = new Set<string>();
  for (const root of roots) skip.add(pathKey(root));
  if (skip.size === 0) return drop;

  // Labelled against the whole drop, before anything is removed, so the
  // sentence about the skipped `police` still says which `police` it was.
  const labels = disambiguateLabels(
    drop.resources.map((resource) => ({
      name: resource.name,
      identity: resource.root,
      origin: drop.origins.get(resource.root),
    })),
    PROSE_LABEL_STYLE,
  );

  const resources: DroppedResource[] = [];
  const origins = new Map(drop.origins);
  const excluded: ExcludedResource[] = [];
  drop.resources.forEach((resource, index) => {
    if (!skip.has(pathKey(resource.root))) {
      resources.push(resource);
      return;
    }
    const origin = drop.origins.get(resource.root);
    origins.delete(resource.root);
    excluded.push({
      name: resource.name,
      root: resource.root,
      ...(origin !== undefined && origin !== "" ? { origin } : {}),
      label: labels[index] ?? resource.name,
      files: resource.files.length,
    });
  });
  if (excluded.length === 0) return drop;

  const truncated = [...drop.truncated];
  for (const entry of excluded) {
    truncated.push(
      `${entry.label} was excluded from this scan by request: none of its ${entry.files.toLocaleString("en-US")} ` +
        `file${entry.files === 1 ? " was" : "s were"} read, so nothing in it was checked.`,
    );
  }

  return {
    resources,
    allPaths: drop.allPaths,
    origins,
    truncated: [...new Set(truncated)],
    excluded: [...(drop.excluded ?? []), ...excluded],
  };
}

// ─── Entry points ────────────────────────────────────────────────────────────

/**
 * A `<input type="file" webkitdirectory>` pick.
 *
 * `webkitRelativePath` already carries the picked folder's name as its first
 * segment, so nothing needs namespacing — that segment is the origin.
 */
export async function fromDirectoryPick(
  files: FileList,
  options?: IntakeOptions,
): Promise<ResourceDrop> {
  const gather = newGather(options);
  ensureNotAborted(gather.signal);
  for (let index = 0; index < files.length; index += 1) {
    ensureNotAborted(gather.signal);
    const file = files[index];
    if (!file) continue;
    const path = dropRelative(relativePathOf(file));
    await addFile(file, path, path.split("/")[0] ?? file.name, gather, 0);
  }
  return assemble(gather);
}

/**
 * The path a picked file carries.
 *
 * `webkitRelativePath` is set by a `webkitdirectory` input and empty for a file
 * picked on its own; it is also absent in environments that do not implement it,
 * which is why this is a guarded read rather than a property access.
 */
function relativePathOf(file: File): string {
  const relative = "webkitRelativePath" in file ? file.webkitRelativePath : "";
  return typeof relative === "string" && relative !== "" ? relative : file.name;
}

/**
 * A drag and drop.
 *
 * Rule 1 lives here: the folders are behind `webkitGetAsEntry()`, and
 * `dataTransfer.files` does not contain them. A tool that reads only `.files`
 * answers a dragged folder — which is the gesture its own copy asks for — with
 * an empty file list and therefore a green all-clear. `.files` is still the
 * fallback for a browser that exposes no entry API, and taking that fallback is
 * said out loud rather than passed off as a complete read.
 *
 * The parameter is typed structurally; see `DataTransferLike`. A real
 * `DataTransfer` satisfies it.
 */
export async function fromDataTransfer(
  dt: DataTransferLike,
  options?: IntakeOptions,
): Promise<ResourceDrop> {
  const gather = newGather(options);
  ensureNotAborted(gather.signal);

  const items = dt.items;
  const entries: DropEntry[] = [];
  let fileItems = 0;
  let refusedItems = 0;

  if (items) {
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      if (!item || item.kind !== "file") continue;
      fileItems += 1;
      const getEntry = item.webkitGetAsEntry;
      const entry = typeof getEntry === "function" ? getEntry.call(item) : null;
      if (entry) entries.push(entry);
      else refusedItems += 1;
    }
  }

  if (entries.length > 0) {
    // Rule 2, reached through the gesture rule 1 exists to serve: two dropped
    // items are two origins.
    //
    // Multi-selecting across parents — a Windows Explorer search-results view is
    // the everyday way to do it — drops two folders that are both called
    // `police` and are not the same folder. Walked from a bare `entry.name` they
    // land on identical paths, the second one's files are dropped as duplicates,
    // and vendor B's spawn codes are gone: the two packs cancel and the report
    // says it is clean. That is the archive defect exactly, so it gets the
    // archive's answer — a namespace per dropped item.
    //
    // Only the colliding items get a container segment, and it is a segment
    // rather than a suffix on the folder's own name on purpose: two resources
    // both named `police` is itself a real finding about a server that can start
    // only one of them, and renaming the second to `police (2)` would erase it.
    const takenRoots = new Set<string>();
    for (const [index, entry] of entries.entries()) {
      const name = dropRelative(entry.name);
      const collides = takenRoots.has(pathKey(name));
      const root = collides ? `dropped-${index + 1}/${name}` : name;
      takenRoots.add(pathKey(name));
      await walkEntry(entry, root, collides ? `dropped-${index + 1}` : name, gather);
    }
    if (refusedItems > 0) {
      gather.truncated.push(
        `${refusedItems} dropped item${refusedItems === 1 ? "" : "s"} could not be read as a file ` +
          "or folder, so nothing in them was checked.",
      );
    }
    return assemble(gather);
  }

  // No entry API, or nothing behind it. `dataTransfer.files` is all that is
  // left, and it contains loose files only — never the contents of a dropped
  // folder. Taking this path is a degraded read and says so, because reporting
  // a dragged folder as empty *and clean* is the competitor defect this module
  // is named after.
  if (fileItems > 0) {
    gather.truncated.push(
      "This browser did not expose the dropped folders, so only loose files were read. " +
        "Use the folder picker instead to check a whole resource.",
    );
  }

  const loose = dt.files;
  if (loose) {
    for (let index = 0; index < loose.length; index += 1) {
      ensureNotAborted(gather.signal);
      const file = loose[index];
      if (!file) continue;
      const path = dropRelative(relativePathOf(file));
      await addFile(file, path, path.split("/")[0] ?? file.name, gather, 0);
    }
  }

  return assemble(gather);
}

/**
 * One or more archives.
 *
 * Each archive namespaces its own contents (rule 2) and opens one level of
 * nesting (rule 3's tail), so a zip of vendor zips works.
 *
 * ── Two archives with the same file name ────────────────────────────────────
 *
 * The namespace used to be the archive's file name and nothing else, which is
 * wrong for the everyday form of the gesture rule 2 is written for: two
 * `police.zip` downloads — one from `Downloads`, one from `Downloads (1)` —
 * selected together in one file input. Both namespaced to `police.zip`, so every
 * entry of the second archive landed on a path the first had already taken,
 * `push` dropped them all as duplicates, and the drop held one resource instead
 * of two. `analyzeResourceSet` then found zero collisions: the green all-clear
 * for the exact input the tool was opened to diagnose. The drop's `truncated`
 * did say the archive appeared twice, but the report itself was clean, and a
 * caveat beside a clean report is not the finding.
 *
 * So the colliding archive gets a container segment, exactly as `fromDataTransfer`
 * gives a colliding dropped folder one. A segment rather than a rename, for the
 * reason argued there: two resources both called `police` is itself the finding,
 * and renaming one would erase it.
 *
 * The origin stays the archive's own file name in both cases. It is the true
 * answer to "which file did this come from" — both really are called
 * `police.zip` — and `labels.ts` is what then tells the two apart in a heading
 * or a sentence, by falling through to the root when the origin cannot. Putting
 * `archive-2` in the origin instead would print an invented file name to a user
 * as if it were on their disk.
 */
export async function fromArchives(files: File[], options?: IntakeOptions): Promise<ResourceDrop> {
  const gather = newGather(options);
  ensureNotAborted(gather.signal);
  const takenRoots = new Set<string>();
  for (const [index, file] of files.entries()) {
    ensureNotAborted(gather.signal);
    const name = dropRelative(file.name);
    const collides = takenRoots.has(pathKey(name));
    takenRoots.add(pathKey(name));
    await addFile(
      file,
      collides ? `archive-${index + 1}/${name}` : name,
      file.name,
      gather,
      0,
      file.name,
    );
  }
  return assemble(gather);
}
